import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowUpRight, Copy, KeyRound, Plus, ShieldOff, Trash2, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiError } from "../../api/client";
import type { Consumer } from "../../api/types";
import { useGatewayPermissions } from "../../auth/permissions";
import { DataState, formatTime, PageHeader, StatusBadge } from "../../components/ui";

interface ConsumerCopyDraft {
  source: Consumer;
  consumer_code: string;
  display_name: string;
}

function suggestedCopyCode(code: string): string {
  return `${code.slice(0, 75)}-copy`;
}

function consumerActionErrorMessage(error: unknown) {
  if (!(error instanceof ApiError)) return error instanceof Error ? error.message : "操作失败，请稍后重试";
  return error.message.replace(/^version conflict:\s*/i, "") || "操作失败，请稍后重试";
}

// ConsumersPage manages consumer identities and provides a safe configuration copy shortcut.
// 管理业务方，并提供不复制 Key 和历史数据的配置复制入口。
export function ConsumersPage() {
  const { can } = useGatewayPermissions();
  const canWrite = can("consumers.write");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const copyCodeInputRef = useRef<HTMLInputElement>(null);
  const consumers = useQuery({ queryKey: ["consumers"], queryFn: api.consumers });
  const [creating, setCreating] = useState(false);
  const [copyDraft, setCopyDraft] = useState<ConsumerCopyDraft | null>(null);
  const [statusFilter, setStatusFilter] = useState<"enabled" | "disabled" | "all">("enabled");
  const [stopTarget, setStopTarget] = useState<Consumer | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Consumer | null>(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [message, setMessage] = useState("");
  const [draft, setDraft] = useState({ consumer_code: "", display_name: "", qps_limit: 100, max_inflight: 500, queue_timeout_seconds: 600 });
  const create = useMutation({
    mutationFn: () => api.createConsumer({ ...draft, status: "enabled" }),
    onSuccess: async () => {
      setCreating(false);
      setDraft({ consumer_code: "", display_name: "", qps_limit: 100, max_inflight: 500, queue_timeout_seconds: 600 });
      await queryClient.invalidateQueries({ queryKey: ["consumers"] });
    },
  });
  const copyConsumer = useMutation({
    mutationFn: (input: ConsumerCopyDraft) => api.copyConsumer(input.source.id, {
      consumer_code: input.consumer_code,
      display_name: input.display_name,
    }),
    onSuccess: async (result) => {
      setCopyDraft(null);
      await queryClient.invalidateQueries({ queryKey: ["consumers"] });
      navigate(`/manage/consumers/${result.data.id}`);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        copyCodeInputRef.current?.focus();
      }
    },
  });
  const copyCodeConflict = copyConsumer.error instanceof ApiError && copyConsumer.error.status === 409;
  const visibleConsumers = useMemo(() => {
    const items = consumers.data?.data || [];
    return statusFilter === "all" ? items : items.filter((consumer) => consumer.status === statusFilter);
  }, [consumers.data?.data, statusFilter]);
  const enabledCount = consumers.data?.data.filter((consumer) => consumer.status === "enabled").length || 0;
  const disabledCount = consumers.data?.data.filter((consumer) => consumer.status === "disabled").length || 0;
  const stopConsumer = useMutation({
    mutationFn: (consumer: Consumer) => api.updateConsumer(consumer.id, {
      consumer_code: consumer.consumer_code,
      display_name: consumer.display_name,
      qps_limit: consumer.qps_limit,
      max_inflight: consumer.max_inflight,
      queue_timeout_seconds: consumer.queue_timeout_seconds,
      status: "disabled",
      expected_updated_at: consumer.updated_at,
    }),
    onSuccess: async () => {
      setStopTarget(null);
      setMessage("业务方已停用，可在“已停用”筛选中查看。");
      await queryClient.invalidateQueries({ queryKey: ["consumers"] });
    },
  });
  const deleteConsumer = useMutation({
    mutationFn: () => api.deleteConsumer(deleteTarget!.id, {
      expected_updated_at: deleteTarget!.updated_at,
      reason: deleteReason.trim(),
      confirm: true,
    }),
    onSuccess: async () => {
      setDeleteTarget(null);
      setDeleteReason("");
      setMessage("业务方已删除。");
      await queryClient.invalidateQueries({ queryKey: ["consumers"] });
    },
  });

  const openCopyDialog = (source: Consumer) => {
    copyConsumer.reset();
    setCopyDraft({
      source,
      consumer_code: suggestedCopyCode(source.consumer_code),
      display_name: `${source.display_name} 副本`,
    });
  };

  return (
    <>
      <PageHeader
        title="业务接入"
        description="创建调用方，并为它配置调用 Key、可用模型、渠道范围、降级策略和渠道额度。"
        actions={canWrite ? <button className="primary-button" onClick={() => setCreating(true)}><Plus size={16} />新增业务方</button> : undefined}
      />
      {message ? <div className="inline-success top-message">{message}</div> : null}
      <DataState loading={consumers.isLoading} error={consumers.error} empty={!consumers.data?.data.length} emptyTitle="尚未接入业务方" emptyDescription="创建业务方后即可生成调用 Key 和添加可用模型">
        <div className="toolbar consumer-list-toolbar">
          <select className="toolbar-select" aria-label="筛选业务方状态" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}>
            <option value="enabled">启用中 ({enabledCount})</option>
            <option value="all">全部 ({consumers.data?.data.length || 0})</option>
            <option value="disabled">已停用 ({disabledCount})</option>
          </select>
          <span className="toolbar-count">显示 {visibleConsumers.length} 个业务方</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>业务方</th><th>状态</th><th>有效 Key</th><th>业务标识记录</th><th>容量</th><th>最后修改</th><th aria-label="操作" /></tr></thead>
            <tbody>
              {visibleConsumers.map((consumer) => {
                const activeKey = consumer.keys.find((key) => key.status === "active");
                return <tr key={consumer.id}>
                  <td><Link className="model-cell" to={`/manage/consumers/${consumer.id}`}><span className="type-icon"><KeyRound size={17} /></span><span><strong>{consumer.display_name}</strong><small>{consumer.consumer_code}</small></span></Link></td>
                  <td><StatusBadge value={consumer.status} /></td>
                  <td>{activeKey ? <><code>{activeKey.key_prefix}••••</code><small className="cell-note">最后使用 {formatTime(activeKey.last_used_at)}</small></> : <span className="muted">尚未生成</span>}</td>
                  <td>{consumer.businesses.length}<small className="cell-note">{consumer.businesses.map((item) => item.business_identifier).slice(0, 2).join("、")}</small></td>
                  <td>{consumer.qps_limit} QPS<small className="cell-note">{consumer.max_inflight} 在途 · 最长等 {consumer.queue_timeout_seconds}s</small></td>
                  <td>{formatTime(consumer.updated_at)}<small className="cell-note">{consumer.updated_by}</small></td>
                  <td>
                    <div className="row-actions">
                      <div className="consumer-row-action-group">
                        {canWrite ? <button type="button" className="consumer-row-action" onClick={() => openCopyDialog(consumer)}><Copy size={14} />复制</button> : null}
                        {canWrite && consumer.status === "enabled" ? <button type="button" className="consumer-row-action danger" onClick={() => { stopConsumer.reset(); setStopTarget(consumer); }}><ShieldOff size={14} />停用</button> : null}
                        {canWrite && consumer.status === "disabled" ? <button type="button" className="consumer-row-action danger" onClick={() => { deleteConsumer.reset(); setDeleteReason(""); setDeleteTarget(consumer); }}><Trash2 size={14} />删除</button> : null}
                        <Link className="consumer-row-action primary" to={`/manage/consumers/${consumer.id}`}>管理 <ArrowUpRight size={14} /></Link>
                      </div>
                    </div>
                  </td>
                </tr>;
              })}
              {!visibleConsumers.length ? <tr><td colSpan={7}><div className="section-empty">当前筛选下没有业务方</div></td></tr> : null}
            </tbody>
          </table>
        </div>
      </DataState>

      {creating ? <div className="dialog-backdrop" role="presentation"><form className="dialog" onSubmit={(event) => { event.preventDefault(); create.mutate(); }}><div className="dialog-header"><div><h2>新增业务方</h2><p>创建后再生成 Key、添加可用模型并分配渠道额度。</p></div><button type="button" className="icon-button" onClick={() => setCreating(false)} aria-label="关闭"><X size={18} /></button></div><div className="form-grid"><label className="field"><span>业务方名称</span><input required maxLength={120} value={draft.display_name} onChange={(event) => setDraft((value) => ({ ...value, display_name: event.target.value }))} /></label><label className="field"><span>业务方代码</span><input required pattern="[a-z0-9][a-z0-9_-]*" value={draft.consumer_code} onChange={(event) => setDraft((value) => ({ ...value, consumer_code: event.target.value }))} /></label><label className="field"><span>QPS 上限</span><input type="number" min={1} value={draft.qps_limit} onChange={(event) => setDraft((value) => ({ ...value, qps_limit: Number(event.target.value) }))} /></label><label className="field"><span>并发任务上限</span><input type="number" min={1} value={draft.max_inflight} onChange={(event) => setDraft((value) => ({ ...value, max_inflight: Number(event.target.value) }))} /></label><label className="field"><span>最长排队时间（秒）</span><input type="number" min={1} value={draft.queue_timeout_seconds} onChange={(event) => setDraft((value) => ({ ...value, queue_timeout_seconds: Number(event.target.value) }))} /></label></div>{create.isError ? <div className="inline-error">{create.error.message}</div> : null}<div className="dialog-actions"><button type="button" className="secondary-button" onClick={() => setCreating(false)}>取消</button><button className="primary-button" disabled={create.isPending}>{create.isPending ? "创建中" : "创建"}</button></div></form></div> : null}

      {copyDraft ? (
        <div className="dialog-backdrop" role="presentation">
          <form
            className="dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="copy-consumer-title"
            onSubmit={(event) => {
              event.preventDefault();
              copyConsumer.mutate(copyDraft);
            }}
          >
            <div className="dialog-header">
              <div>
                <h2 id="copy-consumer-title">复制业务方配置</h2>
                <p>以 {copyDraft.source.display_name} 为模板创建独立业务方。</p>
              </div>
              <button type="button" className="icon-button" disabled={copyConsumer.isPending} onClick={() => setCopyDraft(null)} aria-label="关闭"><X size={18} /></button>
            </div>
            <div className="dialog-body consumer-copy-body">
              <div className="consumer-copy-source">
                <span>配置来源</span>
                <strong>{copyDraft.source.display_name}</strong>
                <code>{copyDraft.source.consumer_code}</code>
              </div>
              <div className="form-grid">
                <label className="field">
                  <span>新业务方名称</span>
                  <input required maxLength={120} autoFocus value={copyDraft.display_name} onChange={(event) => setCopyDraft((value) => value ? { ...value, display_name: event.target.value } : value)} />
                </label>
                <label className="field">
                  <span>新业务方代码</span>
                  <input
                    ref={copyCodeInputRef}
                    required
                    minLength={2}
                    maxLength={80}
                    pattern="[a-z0-9][a-z0-9._-]{1,79}"
                    aria-label="新业务方代码"
                    aria-invalid={copyCodeConflict || undefined}
                    aria-describedby={copyCodeConflict ? "copy-consumer-code-error" : undefined}
                    value={copyDraft.consumer_code}
                    onChange={(event) => setCopyDraft((value) => value ? { ...value, consumer_code: event.target.value } : value)}
                  />
                  {copyCodeConflict ? <small id="copy-consumer-code-error" className="field-error-text" role="alert">业务方代码已存在，请换一个代码后重试。</small> : null}
                </label>
              </div>
              {copyConsumer.isError && !copyCodeConflict ? <div className="inline-error">{copyConsumer.error.message}</div> : null}
            </div>
            <div className="dialog-actions">
              <button type="button" className="secondary-button" disabled={copyConsumer.isPending} onClick={() => setCopyDraft(null)}>取消</button>
              <button className="primary-button" disabled={copyConsumer.isPending}>{copyConsumer.isPending ? "复制中" : "复制并继续配置"}</button>
            </div>
          </form>
        </div>
      ) : null}

      {stopTarget ? <div className="dialog-backdrop" role="presentation"><div className="dialog" role="dialog" aria-modal="true" aria-labelledby="stop-consumer-title"><div className="dialog-header"><div><h2 id="stop-consumer-title">停用业务方</h2><p>{stopTarget.display_name} · {stopTarget.consumer_code}</p></div><button type="button" className="icon-button" disabled={stopConsumer.isPending} onClick={() => setStopTarget(null)} aria-label="关闭"><X size={18} /></button></div><div className="dialog-body consumer-delete-body"><div className="inline-warning"><AlertTriangle size={16} /><span>停用后将拒绝新的模型请求，已有配置和历史记录仍会保留。之后可以在“已停用”筛选中重新管理或删除。</span></div>{stopConsumer.isError ? <div className="inline-error">{consumerActionErrorMessage(stopConsumer.error)}</div> : null}</div><div className="dialog-actions"><button type="button" className="secondary-button" disabled={stopConsumer.isPending} onClick={() => setStopTarget(null)}>取消</button><button type="button" className="danger-button" disabled={stopConsumer.isPending} onClick={() => stopConsumer.mutate(stopTarget)}>{stopConsumer.isPending ? "停用中" : "确认停用"}</button></div></div></div> : null}

      {deleteTarget ? <div className="dialog-backdrop" role="presentation"><form className="dialog" role="dialog" aria-modal="true" aria-labelledby="delete-consumer-title" onSubmit={(event) => { event.preventDefault(); deleteConsumer.mutate(); }}><div className="dialog-header"><div><h2 id="delete-consumer-title">删除业务方</h2><p>{deleteTarget.display_name} · {deleteTarget.consumer_code}</p></div><button type="button" className="icon-button" disabled={deleteConsumer.isPending} onClick={() => setDeleteTarget(null)} aria-label="关闭"><X size={18} /></button></div><div className="dialog-body consumer-delete-body"><div className="inline-warning destructive"><AlertTriangle size={16} /><span>永久删除会清理调用 Key、模型策略、降级步骤和渠道额度。已有请求或用量记录时仍会拒绝删除。</span></div><label className="field"><span>删除原因</span><textarea autoFocus required maxLength={512} rows={3} value={deleteReason} onChange={(event) => setDeleteReason(event.target.value)} placeholder="例如：清理未投入使用的重复业务方" /></label>{deleteConsumer.isError ? <div className="inline-error">{consumerActionErrorMessage(deleteConsumer.error)}</div> : null}</div><div className="dialog-actions"><button type="button" className="secondary-button" disabled={deleteConsumer.isPending} onClick={() => setDeleteTarget(null)}>取消</button><button className="danger-button" disabled={!deleteReason.trim() || deleteConsumer.isPending}>{deleteConsumer.isPending ? "删除中" : "确认永久删除"}</button></div></form></div> : null}
    </>
  );
}
